import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { XCircle, ArrowRight, Eye, EyeOff, Play, Mail, Lock, Key } from '@/utils/icons';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/hooks/useAuth';
import { useUIStore } from '@/stores/uiStore';
import { getDemoAvailable, demoLogin, getGoogleLoginUrl } from '@/services/api';
import type { Tenant, AuthLookupResult } from '@/types/tenant';

type LoginStep = 'email' | 'password' | 'mfa';

export function Login() {
  const navigate = useNavigate();
  const { lookup, legacyLogin } = useAuth();
  const setSession = useUIStore((s) => s.setSession);

  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [enteringDemo, setEnteringDemo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lookupResult, setLookupResult] = useState<AuthLookupResult | null>(null);
  const [demoAvailable, setDemoAvailable] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    getDemoAvailable()
      .then((res) => {
        setDemoAvailable(res.available);
      })
      .catch(() => setDemoAvailable(false));
  }, []);

  // Handle OAuth callback errors
  useEffect(() => {
    const oauthError = searchParams.get('error');
    if (oauthError) {
      const messages: Record<string, string> = {
        oauth_denied: 'Google sign-in was cancelled.',
        oauth_failed: 'Google sign-in failed. Please try again.',
        no_tenant: 'No organization available for sign-in.',
        auth0_denied: 'Sign-in was cancelled.',
        auth0_failed: 'Sign-in failed. Please try again.',
        auth0_not_configured: 'Auth0 login is not yet configured. Contact your administrator.',
        user_not_found: 'Email not recognized. Contact your administrator.',
      };
      setError(messages[oauthError] || 'Authentication failed.');
    }
  }, [searchParams]);

  const handleEmailSubmit = async () => {
    if (!email) return;
    setLoading(true);
    setError(null);

    try {
      const result = await lookup(email);
      setLookupResult(result);

      if (result.authMethod === 'auth0' && result.authUrl) {
        // Redirect to Auth0
        window.location.href = result.authUrl;
        return;
      }

      // Legacy auth — show password field
      setStep('password');
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'code' in err && (err as { code: string }).code === 'USER_NOT_FOUND') {
        setError('Email not recognized. Contact your administrator.');
      } else {
        setError(err instanceof Error ? err.message : 'Lookup failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!email || !password) return;
    setLoading(true);
    setError(null);

    try {
      const result = await legacyLogin(email, password, step === 'mfa' ? otp : undefined);

      if (!result.success) {
        if (result.mfaRequired) {
          setStep('mfa');
          setError(null);
          return;
        }
        setError('Invalid email or password');
        return;
      }

      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setEnteringDemo(true);
    setError(null);
    try {
      const result = await demoLogin();
      setSession({ authenticated: true, tenant: result.tenant as Tenant, isDemo: true });
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enter demo mode');
    } finally {
      setEnteringDemo(false);
    }
  };

  const handleBack = () => {
    setStep('email');
    setPassword('');
    setOtp('');
    setError(null);
    setLookupResult(null);
  };

  return (
    <div className="dls-min-h-screen dls-bg-background dls-flex dls-items-center dls-justify-center dls-p-4 dls-overflow-y-auto">
      <Card className="dls-w-full dls-max-w-lg">
        <CardHeader className="dls-text-center">
          <img
            src="/assets/logos/Neeve-Logo-Lockup-SM_flush.webp"
            alt="Neeve"
            className="dls-h-10 dls-mx-auto dls-mb-4"
          />
          <CardTitle className="dls-text-2xl">Neeve Insights</CardTitle>
          <CardDescription>
            {step === 'email' && 'Sign in to your account'}
            {step === 'password' && `Signing in to ${lookupResult?.organizationName ?? 'your organization'}`}
            {step === 'mfa' && 'Enter your one-time password'}
          </CardDescription>
        </CardHeader>
        <CardContent className="dls-space-y-6">
          {/* Email step */}
          {step === 'email' && (
            <>
              <div className="dls-space-y-4">
                <div className="dls-space-y-2">
                  <label className="dls-text-sm dls-font-medium">Email</label>
                  <div className="dls-relative">
                    <Mail className="dls-absolute dls-left-3 dls-top-1/2 -dls-translate-y-1/2 dls-h-4 dls-w-4 dls-text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="your.email@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleEmailSubmit()}
                      className="dls-pl-10"
                      autoFocus
                    />
                  </div>
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <XCircle className="dls-h-4 dls-w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                className="dls-w-full"
                onClick={handleEmailSubmit}
                disabled={!email || loading}
              >
                {loading ? 'Looking up...' : 'Continue'}
                <ArrowRight className="dls-h-4 dls-w-4 dls-ml-2" />
              </Button>
            </>
          )}

          {/* Password step (legacy SE auth) */}
          {(step === 'password' || step === 'mfa') && (
            <>
              <div className="dls-space-y-4">
                <div className="dls-bg-muted dls-px-3 dls-py-2 dls-rounded-lg dls-text-sm dls-flex dls-items-center dls-gap-2">
                  <Mail className="dls-h-4 dls-w-4 dls-text-muted-foreground dls-flex-shrink-0" />
                  <span className="dls-truncate">{email}</span>
                  <button onClick={handleBack} className="dls-ml-auto dls-text-xs dls-text-muted-foreground hover:dls-text-foreground">
                    Change
                  </button>
                </div>

                <div className="dls-space-y-2">
                  <label className="dls-text-sm dls-font-medium">Password</label>
                  <div className="dls-relative">
                    <Lock className="dls-absolute dls-left-3 dls-top-1/2 -dls-translate-y-1/2 dls-h-4 dls-w-4 dls-text-muted-foreground" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                      className="dls-pl-10 dls-pr-10"
                      autoFocus={step === 'password'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="dls-absolute dls-right-3 dls-top-1/2 -dls-translate-y-1/2 dls-text-muted-foreground hover:dls-text-foreground"
                    >
                      {showPassword ? <EyeOff className="dls-h-4 dls-w-4" /> : <Eye className="dls-h-4 dls-w-4" />}
                    </button>
                  </div>
                </div>

                {step === 'mfa' && (
                  <div className="dls-space-y-2">
                    <label className="dls-text-sm dls-font-medium">One-Time Password</label>
                    <div className="dls-relative">
                      <Key className="dls-absolute dls-left-3 dls-top-1/2 -dls-translate-y-1/2 dls-h-4 dls-w-4 dls-text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Enter OTP from authenticator"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                        className="dls-pl-10"
                        autoFocus
                      />
                    </div>
                  </div>
                )}
              </div>

              {error && (
                <Alert variant="destructive">
                  <XCircle className="dls-h-4 dls-w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="dls-flex dls-gap-3">
                <Button variant="secondary" onClick={handleBack} className="dls-flex-1">
                  Back
                </Button>
                <Button
                  onClick={handlePasswordSubmit}
                  disabled={!password || loading || (step === 'mfa' && !otp)}
                  className="dls-flex-1"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </div>
            </>
          )}

          {/* Divider + alternative auth options (only on email step) */}
          {step === 'email' && (
            <>
              <div className="dls-relative">
                <div className="dls-absolute dls-inset-0 dls-flex dls-items-center">
                  <span className="dls-w-full dls-border-t" />
                </div>
                <div className="dls-relative dls-flex dls-justify-center dls-text-xs dls-uppercase">
                  <span className="dls-bg-background dls-px-2 dls-text-muted-foreground">or</span>
                </div>
              </div>

              <Button
                variant="secondary"
                className="dls-w-full"
                onClick={() => { window.location.href = getGoogleLoginUrl(); }}
              >
                <svg className="dls-h-4 dls-w-4 dls-mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Sign in with Google
              </Button>
              <p className="dls-text-xs dls-text-center dls-text-muted-foreground">
                Neeve employees only
              </p>

              {demoAvailable && (
                <>
                  <Button
                    variant="secondary"
                    className="dls-w-full"
                    onClick={handleDemoLogin}
                    disabled={enteringDemo}
                  >
                    <Play className="dls-h-4 dls-w-4 dls-mr-2" />
                    {enteringDemo ? 'Entering Demo...' : 'Enter Demo Mode'}
                  </Button>
                  <p className="dls-text-xs dls-text-center dls-text-muted-foreground">
                    Explore with sample data from a fictional organization
                  </p>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
